import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useLocation } from "react-router-dom";
import HeroBookingBar from "./HeroBookingBar";

type DockContextValue = {
  /** Eine Hero-Anfrageleiste ist auf dieser Seite montiert. */
  hasHero: boolean;
  /** Die Hero-Leiste ist aus dem Viewport gescrollt – gedockte Leiste einfahren. */
  docked: boolean;
  registerHero: (docked: boolean) => void;
  unregisterHero: () => void;
  /** Geteilte Auswahl beider Anfrageleisten (Hero + gedockt). */
  selection: { vehicleId: string; pickup: string; returnD: string };
  setSelection: (
    next: Partial<{ vehicleId: string; pickup: string; returnD: string }>,
  ) => void;
};

const DockContext = createContext<DockContextValue>({
  hasHero: false,
  docked: false,
  registerHero: () => {},
  unregisterHero: () => {},
  selection: { vehicleId: "", pickup: "", returnD: "" },
  setSelection: () => {},
});

export const BookingDockProvider = ({ children }: { children: ReactNode }) => {
  const [hasHero, setHasHero] = useState(false);
  const [docked, setDocked] = useState(false);
  const [selection, setSelectionState] = useState({
    vehicleId: "",
    pickup: "",
    returnD: "",
  });

  const setSelection = useCallback(
    (next: Partial<{ vehicleId: string; pickup: string; returnD: string }>) =>
      setSelectionState((prev) => ({ ...prev, ...next })),
    [],
  );

  const registerHero = useCallback((isDocked: boolean) => {
    setHasHero(true);
    setDocked(isDocked);
  }, []);

  const unregisterHero = useCallback(() => {
    setHasHero(false);
    setDocked(false);
  }, []);

  const value = useMemo(
    () => ({ hasHero, docked, registerHero, unregisterHero, selection, setSelection }),
    [hasHero, docked, registerHero, unregisterHero, selection, setSelection],
  );

  return <DockContext.Provider value={value}>{children}</DockContext.Provider>;
};

export const useBookingDock = () => useContext(DockContext);

/** Routen ohne Marketing-Header/Anfrage-Kontext. */
const EXCLUDED_PREFIXES = [
  "/admin",
  "/auth",
  "/impressum",
  "/datenschutz",
  "/agb",
  "/qr",
  "/unsubscribe",
  "/oauth",
];

/**
 * True, wenn die gedockte Anfrageleiste aktuell (ab md) sichtbar ist.
 * Wird genutzt, um doppelte CTA-Ebenen zu vermeiden.
 */
export const useDockedBarVisible = () => {
  const { hasHero, docked } = useBookingDock();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    let rafId: number;
    const handleScroll = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => setIsScrolled(window.scrollY > 60));
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      cancelAnimationFrame(rafId);
    };
  }, []);

  if (EXCLUDED_PREFIXES.some((p) => location.pathname.startsWith(p))) return false;
  return hasHero ? docked : isScrolled;
};



/**
 * Kompakte Anfrageleiste, die unter dem Header einfährt, sobald die
 * Hero-Leiste aus dem Blickfeld gescrollt ist. Nur ab md sichtbar –
 * auf Mobil übernimmt die MobileSticky-Leiste am unteren Rand.
 */
const DockedBookingBar = () => {
  const { hasHero, docked } = useBookingDock();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    let rafId: number;
    const handleScroll = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => setIsScrolled(window.scrollY > 60));
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      cancelAnimationFrame(rafId);
    };
  }, []);

  const excluded = EXCLUDED_PREFIXES.some((p) => location.pathname.startsWith(p));
  if (excluded) return null;

  // Ohne Hero-Leiste (z. B. Unterseiten weiter unten) dauerhaft anbieten.
  const show = hasHero ? docked : isScrolled;

  return (
    <div
      aria-hidden={!show}
      className={`hidden md:block fixed left-0 right-0 top-16 md:top-[72px] z-40 overflow-hidden border-b border-border bg-card transition-[max-height,opacity,transform] duration-500 ease-out ${show ? "max-h-32 translate-y-0 opacity-100 shadow-[0_1px_0_0_hsl(var(--border))]" : "pointer-events-none max-h-0 -translate-y-2 opacity-0"}`}
    >
      <div className="container mx-auto px-4 md:px-6 py-2">

        <HeroBookingBar variant="docked" />
      </div>
    </div>
  );
};

export default DockedBookingBar;
